/**
 * Keep the `.multi-accord` listing working after a FacetWP refresh.
 *
 * main.js ініціалізує акордеони рівно один раз, на DOMContentLoaded: він
 * знімає `.multi-accord` і всі `.accord` усередині в локальні змінні й більше
 * до DOM не повертається. Для статичної сторінки цього досить, але на архіві
 * вакансій список живе всередині `.facetwp-template`, і FacetWP замінює його
 * вміст цілком на кожній фільтрації та кожному переході сторінкою. Нові вузли
 * — це вже інші елементи: обробників на них немає, інстансів акордеона теж,
 * тож після рефрешу список просто перестає розкриватись.
 *
 * main.js — збілджений код верстки (§7.5 CLAUDE.md), правити його не можна,
 * тож ініціалізація нових вузлів дублюється тут. Опції й поведінка повторені
 * один в один (accordion-js, duration 400, showMultiple false, взаємне
 * згортання сусідніх груп, зупинка кліків усередині `.ac-panel`) — інакше
 * після фільтрації акордеон поводився б інакше, ніж до неї.
 *
 * Сам `.multi-accord` при цьому лишається на місці: у шаблоні він на
 * `<section>`, поза `.facetwp-template` (archive-vacancies.blade.php), тож
 * рефреш його не чіпає — міняється лише вміст списку.
 *
 * ── Як не ініціалізувати двічі ───────────────────────────────────────────
 *
 * `facetwp-loaded` спрацьовує і на першому завантаженні сторінки, на вузлах,
 * які належать main.js. Друга ініціалізація того самого елемента ламає його
 * тихо: клас `is-active` перемикається, `aria-expanded` виставляється, а
 * висота панелі лишається нульовою — акордеон «клікається», але не
 * розкривається. Тож «вузли з розмітки» треба відрізняти від «вузлів, які
 * підмінив FacetWP», і робиться це без жодної опори на внутрішні маркери
 * accordion-js — лише за тотожністю самих вузлів:
 *
 *   при завантаженні модуля все, що вже є в DOM, заноситься до `known` БЕЗ
 *   ініціалізації — ці вузли належать main.js;
 *
 *   на `facetwp-loaded` ініціалізується рівно те, чого в `known` немає, тобто
 *   те, що з'явилось після засівання.
 *
 * Засівання саме на завантаженні модуля, а НЕ на DOMContentLoaded — і це
 * принципово. Vite віддає бандл як `<script type="module">`, тобто відкладений:
 * він виконується вже після розбору документа, коли вся розмітка на місці, але
 * ще до DOMContentLoaded. А `facetwp-loaded` FacetWP шле зі свого
 * jQuery-`ready`, зареєстрованого раніше за DOMContentLoaded-слухач main.js —
 * тобто ПЕРЕД тим, як main.js хоч щось ініціалізує. Засівання на
 * DOMContentLoaded спізнювалось би саме на цей проміжок, і обидва модулі
 * піднімали б ті самі вузли.
 *
 * Перевірка `readyState` тут не рятує з тієї ж причини: на момент виконання
 * відкладених модулів він уже 'interactive', хоч DOMContentLoaded ще не було.
 * Тому гілка нижче лишається тільки для випадку, коли модуль якимось чином
 * виконали під час розбору документа: тоді розмітки ще немає, засівати нічого,
 * і засів переїжджає на DOMContentLoaded, а до нього обробник мовчить.
 *
 * WeakSet, а не атрибут у розмітці: видалені рефрешем вузли мають зникати
 * разом зі своїм записом, а не накопичуватись.
 */

import Accordion from 'accordion-js';

(function () {
  'use strict';

  const known = new WeakSet();
  const instances = new WeakMap();

  function roots() {
    return document.querySelectorAll('.multi-accord');
  }

  function groups(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.accord'));
  }

  /**
   * Close every open item in the sibling groups of `current`.
   *
   * Список сусідів перечитується на кожному кліку, а не запам'ятовується при
   * ініціалізації: після рефрешу склад групи інший, і збережений масив
   * посилався б на вузли, яких у документі вже немає.
   */
  function closeSiblings(root, current) {
    groups(root).forEach(function (accord) {
      if (accord === current) {
        return;
      }

      const instance = instances.get(accord);

      // Вузли, які ініціалізував main.js, у цій мапі відсутні — їхні інстанси
      // він тримає у себе. Це не проблема: усі `.accord` лежать усередині
      // `.facetwp-template`, тож рефреш замінює їх усі разом, і два покоління
      // вузлів у документі одночасно не трапляються.
      if (!instance) {
        return;
      }

      Array.prototype.slice.call(accord.querySelectorAll('.ac')).forEach(function (item, index) {
        if (item.classList.contains('is-active')) {
          instance.close(index);
        }
      });
    });
  }

  function enhance(root) {
    groups(root).forEach(function (accord) {
      if (known.has(accord)) {
        return;
      }

      known.add(accord);
      instances.set(accord, new Accordion(accord, { duration: 400, showMultiple: false }));

      accord.addEventListener('click', function (e) {
        if (e.target.closest('.ac-trigger')) {
          closeSiblings(root, accord);
        }
      });

      // Клік усередині розкритої панелі не повинен її закривати: `.ac-panel`
      // лежить усередині `.ac-trigger`, тобто всередині самого перемикача.
      accord.querySelectorAll('.ac-panel').forEach(function (panel) {
        panel.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      });
    });
  }

  function seed(root) {
    groups(root).forEach(function (accord) {
      known.add(accord);
    });
  }

  let seeded = false;

  function seedAll() {
    roots().forEach(seed);
    seeded = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seedAll);
  } else {
    seedAll();
  }

  document.addEventListener('facetwp-loaded', function () {
    // Поки не засіяли, всі наявні вузли — чужі: піднімати їх не можна.
    if (!seeded) {
      return;
    }

    roots().forEach(enhance);
  });
})();
